import type { MouseEvent, KeyboardEvent } from 'react';
import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import ShowMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import Blanket from '@atlaskit/blanket';
import { IconButton } from '@atlaskit/button/new';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { LinkItem, MenuGroup, Section } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';
import { Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AutomationIcon from '@atlaskit/icon/core/automation';
import SettingsIcon from '@atlaskit/icon/core/settings';
import ArchiveIcon from '@atlaskit/icon/core/archive-box';
import TrashIcon from '@atlaskit/icon/core/delete';

import { ReverseTrialLozengeListItemExperiment } from '@confluence/trial-components/entry-points/ReverseTrialListItem';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery';
import {
	SPACE_SETTINGS_TRASH_SPACE,
	SPACE_SETTINGS_ARCHIVE_SPACE,
	SPACE_SETTINGS_AUTOMATION,
} from '@confluence/named-routes/entry-points/routes';
import { SpaceWatchButton } from '@confluence/action-buttons/entry-points/spaceWatch';
import { SpaceStarMenuItem } from '@confluence/action-buttons/entry-points/spaceStar';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { fg } from '@confluence/feature-gating';

import { useSpaceSettingsLinkUtils } from './SpaceSettingsLink';
import { SpaceMoreActionsMenuSpaceTools } from './SpaceMoreActionsMenuSpaceTools';

type SpaceMoreActionsMenuProps = {
	spaceId: string;
	spaceKey: string;
	isStarred: boolean;
	isWatched: boolean;
	isSpaceAdmin: boolean;
	canManageSomeUsers: boolean;
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	isSpaceArchived?: boolean;
	hasDeleteSpacePermission?: boolean;
};

export const SpaceMoreActionsMenu = (props: SpaceMoreActionsMenuProps) => {
	const { isLoggedIn } = useSessionData();
	const isNav4Enabled = useIsNav4Enabled();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (!isNav4Enabled) {
		return null;
	}

	const onClick = () => {
		props.setIsOpen(!props.isOpen);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'spaceMoreActionsMenu',
				source: 'spaceNavigation',
				attributes: {
					navigationLayer: 'container',
					navigationContainer: 'sidebar',
					navVersion: '4',
					componentType: 'fixedItemButton',
					itemId: 'meatballDropdown',
					isSpaceAdmin: props.isSpaceAdmin,
				},
			},
		}).fire();
	};

	const onMenuItemClicked =
		(selection: string) => (e: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
			e.stopPropagation();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'spaceMoreActionsMenuItem',
					source: 'spaceMoreActionsMenu',
					attributes: {
						navigationLayer: 'container',
						navigationContainer: 'sidebar',
						navVersion: '4',
						componentType: 'fixedItemAction',
						itemId: selection,
						isSpaceAdmin: props.isSpaceAdmin,
					},
				},
			}).fire();
			props.setIsOpen(false);
		};

	return (
		// TODO: Add `shouldRenderToParent={true}` to meet a11y requirements. See
		// https://a11y.atlassian.net/browse/JCATS-788
		// https://hello.jira.atlassian.cloud/browse/DISCO-4258
		<Popup
			isOpen={props.isOpen}
			onClose={() => props.setIsOpen(false)}
			content={() => {
				return isLoggedIn ? (
					<SpaceMoreActionsMenuContent {...props} onMenuItemClicked={onMenuItemClicked} />
				) : (
					<RestrictedSpaceMoreActionsMenuContent {...props} onMenuItemClicked={onMenuItemClicked} />
				);
			}}
			trigger={(triggerProps) => (
				<>
					<IconButton
						{...triggerProps}
						icon={(iconProps) => <ShowMoreIcon {...iconProps} size="small" />}
						label={<FormattedMessage {...i18n.moreActions} />}
						onClick={onClick}
						spacing="compact"
						appearance="subtle"
						isSelected={props.isOpen}
					/>
					<Blanket
						isTinted={false}
						shouldAllowClickThrough={!props.isOpen}
						onBlanketClicked={() => props.setIsOpen(false)}
					/>
				</>
			)}
			placement="right-start"
		/>
	);
};

type SpaceMoreActionsMenuContentProps = {
	onMenuItemClicked: (
		selection: string,
	) => (e: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => void;
	isSpaceArchived?: boolean;
} & SpaceMoreActionsMenuProps;

const RestrictedSpaceMoreActionsMenuContent = ({
	spaceKey,
	isSpaceAdmin,
	onMenuItemClicked,
}: SpaceMoreActionsMenuContentProps) => {
	const { getHref, fireClickedEvent } = useSpaceSettingsLinkUtils({ spaceKey });

	return (
		<MenuGroup minWidth="240px">
			<Section hasSeparator>
				<LinkItem
					href={getHref({ isSpaceAdmin })}
					iconBefore={<SettingsIcon label="" color="currentColor" />}
					onClick={(e) => {
						fireClickedEvent({ analyticsSource: 'spaceMoreActionsMenu' });
						onMenuItemClicked('spaceSettings')(e);
					}}
					UNSAFE_shouldDisableRouterLink
				>
					<FormattedMessage {...i18n.spaceSettings} />
				</LinkItem>
			</Section>
		</MenuGroup>
	);
};

const SpaceMoreActionsMenuContent = ({
	spaceId,
	spaceKey,
	isStarred,
	isWatched,
	isSpaceAdmin,
	canManageSomeUsers,
	isSpaceArchived,
	onMenuItemClicked,
	hasDeleteSpacePermission,
}: SpaceMoreActionsMenuContentProps) => {
	const { edition } = useSessionData();
	const { getHref, fireClickedEvent } = useSpaceSettingsLinkUtils({ spaceKey });
	const shouldShowTrashSpaceButton = fg('dlp-dlm-data-retention-policy')
		? hasDeleteSpacePermission
		: true;

	return (
		<MenuGroup minWidth="240px">
			<Section>
				<SpaceStarMenuItem
					spaceId={spaceId}
					spaceKey={spaceKey}
					isStarred={isStarred}
					spacing="none"
				/>
				<SpaceWatchButton spaceId={spaceId} isWatched={isWatched} inMenu />
			</Section>
			<SpaceMoreActionsMenuSpaceTools
				canManageSomeUsers={canManageSomeUsers}
				isSpaceAdmin={isSpaceAdmin}
				edition={edition}
				spaceKey={spaceKey}
				onMenuItemClicked={onMenuItemClicked}
			/>
			<Section hasSeparator>
				{isSpaceAdmin && edition !== ConfluenceEdition.PREMIUM && (
					<LinkItem
						href={SPACE_SETTINGS_AUTOMATION.toUrl({ spaceKey })}
						iconAfter={
							<ReverseTrialLozengeListItemExperiment
								touchpointId="confluence_space_settings_automation_list_item"
								edition={ConfluenceEdition.FREE}
							/>
						}
						iconBefore={<AutomationIcon label="" color="currentColor" />}
						onClick={onMenuItemClicked('spaceAutomation')}
						UNSAFE_shouldDisableRouterLink
					>
						<FormattedMessage {...i18n.automation} />
					</LinkItem>
				)}
				<LinkItem
					href={getHref({ isSpaceAdmin })}
					iconBefore={<SettingsIcon label="" color="currentColor" />}
					onClick={(e) => {
						fireClickedEvent({ analyticsSource: 'spaceMoreActionsMenu' });
						onMenuItemClicked('spaceSettings')(e);
					}}
					UNSAFE_shouldDisableRouterLink
				>
					<FormattedMessage {...i18n.spaceSettings} />
				</LinkItem>
			</Section>
			{isSpaceAdmin && (
				<Section hasSeparator>
					<LinkItem
						href={SPACE_SETTINGS_ARCHIVE_SPACE.toUrl({ spaceKey })}
						iconBefore={<ArchiveIcon label="" color="currentColor" />}
						onClick={onMenuItemClicked('spaceArchive')}
						UNSAFE_shouldDisableRouterLink
					>
						{isSpaceArchived ? (
							<FormattedMessage {...i18n.restoreSpace} />
						) : (
							<FormattedMessage {...i18n.archiveSpace} />
						)}
					</LinkItem>
					{shouldShowTrashSpaceButton && (
						<LinkItem
							href={SPACE_SETTINGS_TRASH_SPACE.toUrl({ spaceKey })}
							iconBefore={<TrashIcon label="" color={token('color.text.danger')} />}
							onClick={onMenuItemClicked('spaceDelete')}
							UNSAFE_shouldDisableRouterLink
						>
							<Text color="color.text.danger">
								<FormattedMessage {...i18n.trashSpace} />
							</Text>
						</LinkItem>
					)}
				</Section>
			)}
		</MenuGroup>
	);
};

const i18n = defineMessages({
	moreActions: {
		id: 'side-navigation.space-navigation.more-actions',
		defaultMessage: 'More actions',
		description:
			'Accessible label text for the ... button which opens a menu of additional actions for a space. Appears next to the space title.',
	},
	addUsers: {
		id: 'side-navigation.space-navigation.users',
		defaultMessage: 'Users',
		description:
			'Menu item to navigate to the space permissions tab in space settings that allows you to add users to a space. Appears in the space more actions menu.',
	},
	automation: {
		id: 'side-navigation.space-navigation.automation',
		defaultMessage: 'Automation',
		description:
			'Menu item to navigate to the automation page in space settings. Appears in the space more actions menu.',
	},
	spaceSettings: {
		id: 'side-navigation.space-navigation.space-settings',
		defaultMessage: 'Space settings',
		description: 'Menu item to navigate to space settings. Appears in the space more actions menu.',
	},
	archiveSpace: {
		id: 'side-navigation.space-navigation.archive-space',
		defaultMessage: 'Archive space',
		description:
			'Menu item to archive a space. Links to the archive space settings page. Appears in the space more actions menu.',
	},
	restoreSpace: {
		id: 'side-navigation.space-navigation.restore-space',
		defaultMessage: 'Restore space',
		description:
			'Menu item to restore an archived space. Links to the archive space settings page. Appears in the space more actions menu.',
	},
	trashSpace: {
		id: 'side-navigation.space-navigation.trash-space',
		defaultMessage: 'Delete space',
		description:
			'Menu item to trash a space. Links to the trash space settings page. Appears in the space more actions menu. For unclear reasons, a decision was made to use the word "Delete" isntead of "Trash". This button does not permanently delete the space, it allows the user to move it to the trash.',
	},
});
